import React, { useState } from 'react';
import Papa from 'papaparse';
import axios from 'axios';
import GLOBALVAR from '../constants';
import { Toast, ToastContainer, Card, Form, Button, ProgressBar, Alert } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { format, parse, isValid } from 'date-fns';

const ImportLoanPayments = () => {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({ type: '', message: '' });
  const [validationError, setValidationError] = useState('');
  const [processedCount, setProcessedCount] = useState(0);
  const [totalRows, setTotalRows] = useState(0);

  const validateFile = (file) => {
    if (!file) return 'Please select a file';
    if (file.type !== 'text/csv') return 'Please select a CSV file';
    if (file.size > 5 * 1024 * 1024) return 'File size should be less than 5MB';
    return '';
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    const error = validateFile(selectedFile);
    
    if (error) {
      setValidationError(error);
      setFile(null);
      showNotification(error, 'error');
      return;
    }

    setFile(selectedFile);
    setValidationError('');
    setErrors([]);
    setProgress(0);
  };

  const showNotification = (message, type = 'success') => {
    setToastMessage({ type, message });
    setShowToast(true);
  };

  const handleImport = async () => {
    if (!file) {
      showNotification('Please select a file first!', 'error');
      return;
    }

    setIsImporting(true);
    setErrors([]);
    setProgress(0);
    setProcessedCount(0);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        if (results.errors.length > 0) {
          setErrors(prev => [...prev, ...results.errors.map(error => `CSV Parse Error: ${error.message}`)]);
          setIsImporting(false);
          showNotification('Error parsing CSV file', 'error');
          return;
        }

        const data = results.data;
        setTotalRows(data.length);

        for (let i = 0; i < data.length; i++) {
          const row = data[i];
          
          // Validate required fields
          const requiredFields = ['Loan Date', 'Loan Amount', 'Payment Terms', 'Payment Amount'];
          const missingFields = requiredFields.filter(field => !row[field]);
          
          if (missingFields.length > 0) {
            setErrors(prev => [...prev, `Row ${i + 1}: Missing required fields: ${missingFields.join(', ')}`]);
            continue;
          }

          try {
            // Validate payment date format
            const paymentDate = parse(row['Payment Date'], 'MM/dd/yyyy', new Date());
            
            if (!isValid(paymentDate)) {
              setErrors(prev => [...prev, `Row ${i + 1}: Invalid payment date format. Please use MM/DD/YYYY format.`]);
              continue;
            }

            const loanProcessingResponse = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/payments/check`, {
              params: {
                dateGranted: row['Loan Date'],
                amountGranted: parseFloat(row['Loan Amount']).toFixed(2),
                loanTerm: parseInt(row['Payment Terms']),
                lastName: row['Last Name']
              }
            });

            if (loanProcessingResponse.data.exists) {
              // Check if multiple records were found
              if (loanProcessingResponse.data.count > 1) {
                const loanDetails = loanProcessingResponse.data.loans
                  .map(loan => `\n- ${loan.applicantName} (Balance: ${loan.remainingBalance})`)
                  .join('');
                
                setErrors(prev => [...prev, `Row ${i + 1}: Multiple loan records found (${loanProcessingResponse.data.count} records) for Date: ${row['Loan Date']}, Amount: ${row['Loan Amount']}, Term: ${row['Payment Terms']}. Matches:${loanDetails}`]);
                continue;
              }

              const loan = loanProcessingResponse.data.loans[0];
              const paymentAmount = parseFloat(parseFloat(row['Payment Amount']).toFixed(2));
              const paidPenalty = parseFloat(parseFloat(row['Penalty'] || 0).toFixed(2));

              // Calculate interest based on loan details
              const monthlyInterestRate = (loan.interestRate / 100);
              const paidInterest = parseFloat((paymentAmount * monthlyInterestRate).toFixed(2));
              const paidPrincipal = parseFloat((paymentAmount - paidInterest).toFixed(2));
              const paidOther = 0.00;

              // Calculate total after ensuring all components have 2 decimal places
              const paidTotal = parseFloat((paidPrincipal + paidInterest + paidPenalty + paidOther).toFixed(2));

              const paymentPayload = {
                loanProcessingID: loan.id,
                paymentDate: format(paymentDate, 'yyyy-MM-dd'),
                prNumber: row['PR Number'] || '',
                orNumber: row['OR Number'] || '',
                paymentType: 'cash',
                chequeNumber: row['Cheque Number'] || '',
                paidInterest: paidInterest.toFixed(2),
                paidPrincipal: paidPrincipal.toFixed(2),
                paidPenalty: paidPenalty.toFixed(2),
                paidOther: paidOther.toFixed(2),
                paidTotal: paidTotal.toFixed(2)
              };

              // Add validation to ensure the sum matches
              const sumComponents = parseFloat((paidPrincipal + paidInterest + paidPenalty + paidOther).toFixed(2));
              if (sumComponents !== paidTotal) {
                setErrors(prev => [...prev, 
                  `Row ${i + 1}: Payment components don't sum to total (${sumComponents} != ${paidTotal}). ` +
                  `Components: Principal=${paidPrincipal}, Interest=${paidInterest}, ` +
                  `Penalty=${paidPenalty}, Other=${paidOther}`
                ]);
                continue;
              }

              await axios.post(`${GLOBALVAR['SERVER_ROOT']}/api/payments/add`, paymentPayload);
            } else {
              setErrors(prev => [...prev, `Row ${i + 1}: No loan processing entry found for Date: ${row['Loan Date']}, Amount: ${row['Loan Amount']}, Term: ${row['Payment Terms']}`]);
            }
          } catch (error) {
            setErrors(prev => [...prev, `Row ${i + 1}: ${error.message}`]);
          }
          
          setProcessedCount(i + 1);
          setProgress(((i + 1) / data.length) * 100);
        }

        setIsImporting(false);
        showNotification(
          errors.length === 0 
            ? 'CSV import completed successfully!' 
            : `Import completed with ${errors.length} errors`,
          errors.length === 0 ? 'success' : 'warning'
        );
      },
      error: (error) => {
        setIsImporting(false);
        setErrors(prev => [...prev, `Error parsing CSV: ${error.message}`]);
        showNotification(`Error parsing CSV: ${error.message}`, 'error');
      }
    });
  };

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <Card className="shadow">
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">Import Loan Payments</h4>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-4">
                <Form.Label>Choose CSV File</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    disabled={isImporting}
                    isInvalid={!!validationError}
                  />
                  <Button
                    variant="primary"
                    onClick={handleImport}
                    disabled={!file || isImporting}
                  >
                    {isImporting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Importing...
                      </>
                    ) : (
                      'Import CSV'
                    )}
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {validationError}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>

              {isImporting && (
                <div className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                        <small>Processing: {processedCount} of {totalRows} rows</small>
                        <small>{Math.round(progress)}%</small>
                    </div>
                    <ProgressBar
                        animated
                        striped
                        now={progress}
                        variant={errors.length > 0 ? "warning" : "primary"}
                    />
                </div>
              )}

              {errors.length > 0 && (
                <Alert variant="warning">
                    <Alert.Heading>
                        Import Errors {isImporting ? `(${errors.length})` : `(${errors.length} total)`}:
                    </Alert.Heading>
                    <hr />
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        <ul className="mb-0">
                            {errors.map((error, index) => (
                                <li key={index} className="mb-2">
                                    {error}
                                    <small className="text-muted d-block">
                                        {new Date().toLocaleTimeString()}
                                    </small>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      <ToastContainer position="top-end" className="p-3">
        <Toast 
          show={showToast} 
          onClose={() => setShowToast(false)}
          bg={toastMessage.type === 'error' ? 'danger' : toastMessage.type}
          delay={3000}
          autohide
        >
          <Toast.Header closeButton>
            <strong className="me-auto">Import Status</strong>
          </Toast.Header>
          <Toast.Body className={toastMessage.type === 'error' ? 'text-white' : ''}>
            {toastMessage.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default ImportLoanPayments;
